import React, { useState, useEffect, useCallback } from "react";
import { collection, getDocs, updateDoc, doc, deleteField } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthProvider";
import GlobalWorkout from "../models/GlobalWorkout";
import { WORKOUT_CATEGORIES, getCategoryById } from "../config/workoutCategories";
import { validateWorkout, SCORE_TYPES } from "../schemas/workout";

const ADMIN_EMAIL = "mrdard2@gmail.com";

const defaultsForCategory = (categoryId) => {
  const cat = getCategoryById(categoryId);
  return cat.timerDefaults || {};
};

const SCORE_TYPE_OPTIONS = [
  { id: "time",   label: "Time (minutes)" },
  { id: "rounds", label: "Rounds" },
  { id: "reps",   label: "Reps" },
  { id: "weight", label: "Weight (kg)" },
];

const EMPTY_FORM = {
  name: "",
  description: "",
  category: "EMOM",
  scoreType: "rounds",
  exercises: [{ name: "", sets: "", targetReps: "", notes: "" }],
  rounds: null,
  intervalSec: null,
  capSec: null,
  workSec: null,
  restSec: null,
  isDeathBy: false,
};

const AdminPage = () => {
  const { user } = useAuth();
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState("");

  const fetchWorkouts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await GlobalWorkout.getAll({ requirePopularity: false });
      setWorkouts(data);
    } catch (err) {
      setError("Failed to load workouts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkouts();
  }, [fetchWorkouts]);

  const filteredWorkouts =
    activeCategory === "All"
      ? workouts
      : workouts.filter((w) => w.category === activeCategory);

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError("");
    setJsonMode(false);
    setJsonText("");
    setShowModal(true);
  };

  const openEdit = (workout) => {
    setEditingId(workout.id);
    const td = defaultsForCategory(workout.category || "Other");
    setForm({
      name: workout.name || "",
      description: workout.description || "",
      category: workout.category || "Other",
      scoreType: workout.scoreType || "rounds",
      exercises:
        workout.exercises?.length > 0
          ? workout.exercises.map((e) => ({
              name: e.name || "",
              sets: e.sets ?? "",
              targetReps: e.targetReps ?? "",
              notes: e.notes || "",
            }))
          : [{ name: "", sets: "", targetReps: "", notes: "" }],
      rounds: workout.rounds ?? td.rounds ?? null,
      intervalSec: workout.intervalSec ?? td.intervalSec ?? null,
      capSec: workout.capSec ?? td.capSec ?? null,
      workSec: workout.workSec ?? td.workSec ?? null,
      restSec: workout.restSec ?? td.restSec ?? null,
      isDeathBy: workout.isDeathBy ?? false,
    });
    setError("");
    setShowModal(true);
  };

  const importJson = () => {
    let obj;
    try {
      obj = JSON.parse(jsonText);
    } catch {
      setError("Invalid JSON syntax");
      return;
    }

    const validation = validateWorkout(obj);
    if (!validation.success) {
      setError(`Schema validation: ${validation.error}`);
      return;
    }

    const parsed = validation.data;
    const exercises =
      parsed.exercises.length > 0
        ? parsed.exercises.map((e) => ({
            name: e.name || "",
            sets: e.sets ?? "",
            targetReps: e.targetReps ?? "",
            notes: e.notes || "",
          }))
        : [{ name: "", sets: "", targetReps: "", notes: "" }];

    setForm({
      name: parsed.name,
      description: parsed.description,
      category: parsed.category,
      scoreType: parsed.scoreType,
      exercises,
      rounds: parsed.rounds ?? null,
      intervalSec: parsed.intervalSec ?? null,
      capSec: parsed.capSec ?? null,
      workSec: parsed.workSec ?? null,
      restSec: parsed.restSec ?? null,
      isDeathBy: parsed.isDeathBy ?? false,
    });
    setJsonMode(false);
    setError("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const exercises = form.exercises
        .filter((ex) => ex.name.trim())
        .map((ex) => ({
          name: ex.name.trim(),
          sets: ex.sets !== "" && ex.sets != null ? Number(ex.sets) : null,
          targetReps: ex.targetReps
            ? isNaN(Number(ex.targetReps))
              ? ex.targetReps
              : Number(ex.targetReps)
            : null,
          notes: ex.notes || "",
        }));

      const td = defaultsForCategory(form.category);

      // Build schema data with only fields relevant to the category
      const schemaData = {
        name: form.name.trim(),
        description: form.description.trim(),
        category: form.category,
        scoreType: form.scoreType,
        exercises,
        isDeathBy: form.isDeathBy,
      };

      // Add category-specific timer fields
      if ('rounds' in td && form.rounds != null && form.rounds !== "")
        schemaData.rounds = Number(form.rounds);
      if ('intervalSec' in td && form.intervalSec != null && form.intervalSec !== "")
        schemaData.intervalSec = Number(form.intervalSec);
      if ('capSec' in td || ['ForTime', 'Endurance', 'Chipper', 'Other'].includes(form.category)) {
        if (form.capSec != null && form.capSec !== "")
          schemaData.capSec = Number(form.capSec);
      }
      if ('workSec' in td && form.workSec != null && form.workSec !== "")
        schemaData.workSec = Number(form.workSec);
      if ('restSec' in td && form.restSec != null && form.restSec !== "")
        schemaData.restSec = Number(form.restSec);

      const validation = validateWorkout(schemaData);
      if (!validation.success) {
        setError(validation.error);
        setSaving(false);
        return;
      }

      if (editingId) {
        await GlobalWorkout.update(editingId, validation.data);
      } else {
        await GlobalWorkout.create(validation.data);
      }
      setShowModal(false);
      await fetchWorkouts();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (workoutId) => {
    try {
      await GlobalWorkout.delete(workoutId);
      setDeleteConfirm(null);
      await fetchWorkouts();
    } catch (err) {
      setError(err.message);
    }
  };

  const updateExercise = (index, field, value) => {
    const updated = [...form.exercises];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, exercises: updated });
  };

  const addExercise = () => {
    setForm({
      ...form,
      exercises: [...form.exercises, { name: "", sets: "", targetReps: "", notes: "" }],
    });
  };

  const removeExercise = (index) => {
    if (form.exercises.length > 1) {
      setForm({
        ...form,
        exercises: form.exercises.filter((_, i) => i !== index),
      });
    }
  };

  const fmtSec = (s) => {
    const n = Number(s);
    if (!n || n <= 0) return null;
    const m = Math.floor(n / 60);
    const r = n % 60;
    return m > 0 ? `${m}:${String(r).padStart(2, "0")} min` : `${r}s`;
  };

  const inputClass =
    "w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent";
  const labelClass =
    "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1";

  if (!user || user.email !== ADMIN_EMAIL) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Access Denied</h1>
        <p className="text-gray-500 dark:text-gray-400">You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Admin - Global Workouts
        </h1>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              const snapshot = await getDocs(collection(db, 'globalWorkouts'));
              let count = 0;
              for (const docSnap of snapshot.docs) {
                const data = docSnap.data();
                const cat = data.category || data.workoutCategory;
                if (cat === 'Tabata') {
                  await updateDoc(doc(db, 'globalWorkouts', docSnap.id), {
                    category: 'HIIT',
                    workoutCategory: deleteField(),
                    durationType: deleteField(),
                    measurementType: deleteField(),
                    measurementUnit: deleteField(),
                  });
                  count++;
                }
              }
              alert(`Migrated ${count} Tabata doc(s) to HIIT`);
              fetchWorkouts();
            }}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors text-sm"
          >
            Migrate Tabata
          </button>
          <button
            onClick={openAdd}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
          >
            + Add Workout
          </button>
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setActiveCategory("All")}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            activeCategory === "All"
              ? "bg-indigo-600 text-white"
              : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
          }`}
        >
          All ({workouts.length})
        </button>
        {WORKOUT_CATEGORIES.map((cat) => {
          const count = workouts.filter(
            (w) => w.category === cat.id
          ).length;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeCategory === cat.id
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              {cat.icon} {cat.name} ({count})
            </button>
          );
        })}
      </div>

      {/* Workout list */}
      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          Loading workouts...
        </div>
      ) : filteredWorkouts.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          No workouts in this category.
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredWorkouts.map((workout) => {
            const cat = getCategoryById(workout.category);
            return (
              <div
                key={workout.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 flex items-center justify-between"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{cat.icon}</span>
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                      {workout.name}
                    </h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                      {workout.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[700px]">
                    {(workout.description || "No description").slice(0, 80)}
                    {(workout.description || "").length > 80 ? "..." : ""}
                    {workout.exercises?.length > 0 &&
                      ` | ${workout.exercises.length} exercise${
                        workout.exercises.length !== 1 ? "s" : ""
                      }`}
                    {" | Pop: " + (workout.popularity || 0)}
                  </p>
                </div>
                <div className="flex gap-2 ml-4 shrink-0">
                  <button
                    onClick={() => openEdit(workout)}
                    className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Edit
                  </button>
                  {deleteConfirm === workout.id ? (
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleDelete(workout.id)}
                        className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(workout.id)}
                      className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingId ? "Edit Workout" : "Add Workout"}
              </h2>
              {!editingId && (
                <button
                  type="button"
                  onClick={() => { setJsonMode(!jsonMode); setError(""); }}
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  {jsonMode ? "Back to Form" : "Paste JSON"}
                </button>
              )}
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-lg">
                {error}
              </div>
            )}

            {jsonMode ? (
              <div>
                <textarea
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  rows={16}
                  placeholder={'{\n  "name": "Workout Name",\n  "description": "...",\n  "category": "HIIT",\n  "scoreType": "rounds",\n  "workSec": 30,\n  "restSec": 30,\n  "rounds": 5,\n  "exercises": [\n    { "name": "Exercise", "targetReps": 10, "notes": "" }\n  ]\n}'}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent mb-4"
                />
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={importJson}
                    className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Convert
                  </button>
                </div>
              </div>
            ) : (

            <form onSubmit={handleSave}>
              <div className="mb-4">
                <label className={labelClass}>Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputClass}
                  required
                />
              </div>

              <div className="mb-4">
                <label className={labelClass}>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  rows={2}
                  className={inputClass}
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelClass}>Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => {
                      const newCat = e.target.value;
                      const td = defaultsForCategory(newCat);
                      setForm({
                        ...form,
                        category: newCat,
                        rounds: td.rounds ?? null,
                        intervalSec: td.intervalSec ?? null,
                        capSec: td.capSec ?? null,
                        workSec: td.workSec ?? null,
                        restSec: td.restSec ?? null,
                      });
                    }}
                    className={inputClass}
                  >
                    {WORKOUT_CATEGORIES.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Score Type</label>
                  <select
                    value={form.scoreType}
                    onChange={(e) =>
                      setForm({ ...form, scoreType: e.target.value })
                    }
                    className={inputClass}
                  >
                    {SCORE_TYPE_OPTIONS.map((st) => (
                      <option key={st.id} value={st.id}>
                        {st.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* EMOM: isDeathBy checkbox */}
              {form.category === 'EMOM' && (
                <div className="mb-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={form.isDeathBy}
                      onChange={(e) => setForm({ ...form, isDeathBy: e.target.checked })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Death By (escalating reps each round)
                  </label>
                </div>
              )}

              {/* Category-specific timer fields */}
              {(() => {
                const td = defaultsForCategory(form.category);
                const hasRounds = 'rounds' in td;
                const hasIntervalSec = 'intervalSec' in td;
                const hasCapSec = 'capSec' in td || ['ForTime', 'Endurance', 'Chipper', 'Other'].includes(form.category);
                const hasWorkSec = 'workSec' in td;
                const hasRestSec = 'restSec' in td;
                if (!hasRounds && !hasIntervalSec && !hasCapSec && !hasWorkSec && !hasRestSec) return null;

                return (
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {hasRounds && (
                      <div>
                        <label className={labelClass}>Rounds</label>
                        <input
                          type="number"
                          value={form.rounds ?? ""}
                          onChange={(e) =>
                            setForm({ ...form, rounds: e.target.value })
                          }
                          min="1"
                          max="100"
                          className={inputClass}
                        />
                      </div>
                    )}
                    {hasIntervalSec && (
                      <div>
                        <label className={labelClass}>Interval (seconds)</label>
                        <input
                          type="number"
                          value={form.intervalSec ?? ""}
                          onChange={(e) =>
                            setForm({ ...form, intervalSec: e.target.value })
                          }
                          min="10"
                          className={inputClass}
                        />
                        {fmtSec(form.intervalSec) && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">{fmtSec(form.intervalSec)}</span>
                        )}
                      </div>
                    )}
                    {hasCapSec && (
                      <div>
                        <label className={labelClass}>Time Cap (seconds)</label>
                        <input
                          type="number"
                          value={form.capSec ?? ""}
                          onChange={(e) =>
                            setForm({ ...form, capSec: e.target.value })
                          }
                          min="1"
                          className={inputClass}
                        />
                        {fmtSec(form.capSec) && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">{fmtSec(form.capSec)}</span>
                        )}
                      </div>
                    )}
                    {hasWorkSec && (
                      <div>
                        <label className={labelClass}>Work (seconds)</label>
                        <input
                          type="number"
                          value={form.workSec ?? ""}
                          onChange={(e) =>
                            setForm({ ...form, workSec: e.target.value })
                          }
                          min="5"
                          className={inputClass}
                        />
                        {fmtSec(form.workSec) && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">{fmtSec(form.workSec)}</span>
                        )}
                      </div>
                    )}
                    {hasRestSec && (
                      <div>
                        <label className={labelClass}>Rest (seconds)</label>
                        <input
                          type="number"
                          value={form.restSec ?? ""}
                          onChange={(e) =>
                            setForm({ ...form, restSec: e.target.value })
                          }
                          min="5"
                          className={inputClass}
                        />
                        {fmtSec(form.restSec) && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">{fmtSec(form.restSec)}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Exercises */}
              <div className="mb-4">
                <label className={labelClass}>Exercises</label>
                <div className="space-y-2">
                  {form.exercises.map((exercise, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <input
                        type="text"
                        value={exercise.name}
                        onChange={(e) =>
                          updateExercise(index, "name", e.target.value)
                        }
                        placeholder="Exercise name"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      {form.category === 'Strength' && (
                        <input
                          type="number"
                          value={exercise.sets ?? ""}
                          onChange={(e) =>
                            updateExercise(index, "sets", e.target.value)
                          }
                          placeholder="Sets"
                          min="1"
                          className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      )}
                      <input
                        type="text"
                        value={exercise.targetReps}
                        onChange={(e) =>
                          updateExercise(index, "targetReps", e.target.value)
                        }
                        placeholder="Reps"
                        className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        value={exercise.notes}
                        onChange={(e) =>
                          updateExercise(index, "notes", e.target.value)
                        }
                        placeholder="Notes"
                        className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      {form.exercises.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeExercise(index)}
                          className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addExercise}
                  className="mt-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  + Add exercise
                </button>
              </div>

              <div className="flex gap-4 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-lg font-medium transition-colors"
                >
                  {saving
                    ? "Saving..."
                    : editingId
                    ? "Update Workout"
                    : "Create Workout"}
                </button>
              </div>
            </form>

            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
